import { Col, Input, Reactions, ReactToPost, Row } from 'components';
import { getCalculatedText, updateViewsCount as updateViewsCountUtil } from 'core';
import { Api, formatText, theme } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SuggestionItem } from './';

let timer;
const Footer = ({
  post = {},
  comments,
  setComments,
  onHandleUpdateProfileViewsCount,
  setIsAPILoading,
  setCurrentPostViewsCount
}) => {
  const [reactionsVisible, setReactionsVisible] = useState(false),
    [reactionDelay, setReactionDelay] = useState(false),
    [followDelay, setFollowDelay] = useState(false),
    [commentText, setCommentText] = useState(''),
    [antiSpam, setAntiSpam] = useState(false),
    [suggestionResults, setSuggestionResults] = useState([]),
    [tagged, setTagged] = useState([]),
    user = useSelector((state) => state.user),
    { t } = useTranslation(),
    dispatch = useDispatch(),
    { commentsCount, owner, createdAt } = post,
    prevReaction = user?.reactions?.find((x) => x.entityId === post?._id)?.reaction,
    [currentReaction, setCurrentReaction] = useState(prevReaction);

  useEffect(() => {
    let lastAt = commentText.lastIndexOf('@'),
      lastSpace = commentText.lastIndexOf(' ');

    if (lastAt !== -1 && lastSpace < lastAt) {
      clearTimeout(timer);

      const text = commentText.slice(lastAt + 1);
      if (!text.length) return setSuggestionResults([]);

      timer = setTimeout(async () => {
        const res = await Api.post('/users/tagSearch', { text, contentType: 'mention' });
        if (!res) return;

        setSuggestionResults(res);
      }, 300);

      return;
    }

    setSuggestionResults([]);
  }, [commentText]);

  async function handleSend() {
    if (antiSpam) return;

    const taggedUsers = tagged.filter((x) =>
      formatText(commentText)
        .filter((x) => x.type === 'mention')
        .map((x) => x.text.slice(1))
        .includes(x.username)
    );

    setAntiSpam(true);
    const res = await Api.post('/comments/create', {
      entityType: 'post',
      entityId: post?._id,
      body: commentText,
      taggedUsers
    });
    if (!res) return null;

    const newComment = {
      ...res,
      owner: user,
      calculatedText: await getCalculatedText(res.body, {
        width: 355,
        maxLength: 860,
        numberOfLines: 2,
        addToStart: user.name + ' '
      })
    };

    dispatch({
      type: 'INC_COMMENT_COUNT',
      payload: {
        postId: post?._id
      }
    });

    setCommentText('');
    setAntiSpam(false);
    setComments((comments) => [newComment, ...comments]);

    // Fire API request AFTER modal is visible
    const payload = {
      trigger: 'seeAll',
      postId: null,
      ownerId: post.owner?._id
    };

    if (onHandleUpdateProfileViewsCount && typeof onHandleUpdateProfileViewsCount === 'function') {
      onHandleUpdateProfileViewsCount(); // update post owner current views when commenting
    }

    // update owner profile views count
    setTimeout(() => updateViewCount(payload), 0);
  }

  const updateViewCount = async ({ trigger, postId, ownerId }) => {
    // prevent updating of profile views for the same owner
    if (ownerId === user?._id) return;

    try {
      const payload = {
        userId: ownerId,
        trigger,
        postId
      };

      // update profile views
      const res = await updateViewsCountUtil({ payload, currentUserId: user._id });
    } catch (err) {
      const message = err.response?.data
        ? err.response.data
        : 'Something went wrong.\nPlease try again later.';

      dispatch({
        type: 'HANDLE_RES',
        payload: { err: message }
      });
    }
  };

  async function react(reaction) {
    if (reactionDelay) return;

    setReactionDelay(true);
    setTimeout(() => setReactionsVisible(false), 10);

    setCurrentReaction((prevState) => (prevState === reaction ? null : reaction));
    await updateViewsCountOnReact(reaction);

    const data = {
      reaction,
      prevReaction,
      entityId: post?._id,
      entityType: 'post',
      userId: user?._id
    };

    dispatch({
      type: 'ADD_REACTION_TO_POST',
      payload: data
    });

    setReactionDelay(false);

    const res = await Api.post('/users/react', data);

    if (!res) return false;
  }

  async function updateViewsCountOnReact(reaction) {
    // perform update views count ---------------
    if (currentReaction === reaction) {
      return;
    }

    try {
      if (
        onHandleUpdateProfileViewsCount &&
        typeof onHandleUpdateProfileViewsCount === 'function'
      ) {
        onHandleUpdateProfileViewsCount(); // update post owner current views when commenting
      }

      const payload = {
        trigger: 'react',
        postId: post._id,
        ownerId: post.owner?._id
      };

      setIsAPILoading(true);
      setCurrentPostViewsCount((prevState) => prevState + 1);

      await updateViewCount(payload);
    } catch (err) {
      throw err;
    } finally {
      setIsAPILoading(false);
    }
  }

  function handleReply(replyTo) {
    setCommentText(
      `${commentText.substring(0, commentText.lastIndexOf('@'))}@${replyTo.username} `
    );
    setTagged([
      ...tagged,
      {
        username: replyTo.username,
        id: replyTo._id
      }
    ]);
  }

  return (
    <Col noFlex wid="calc(100%)" pad="16px 0 16px 0" borderTop={`1px solid ${theme.GREY_20}`}>
      <Row center start pad="0 0 0 24px">
        <ReactToPost
          post
          reactionsVisible={reactionsVisible}
          setReactionsVisible={setReactionsVisible}
          handleSubmit={(e) => react(e)}
          reaction={prevReaction}
        />

        <Row marg="0 0 0 27px">
          <Reactions entity={post?.reactionCounts} />
        </Row>
      </Row>

      <Row
        borderTop={`1px solid ${theme.GREY_20}`}
        pad="16px 16px 0 24px"
        marg="16px 0 0"
        pos="relative"
      >
        {!!suggestionResults.length && (
          <Col
            start
            noFlex
            pos="absolute"
            bg="#fff"
            pad="11px 0"
            bottom="44px"
            wid="calc(100% - 40px)"
            scrollY
            style={{
              boxShadow: '0px -10px 16px rgba(0,0,0,0.2)',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              scrollbarWidth: 'none'
            }}
            maxHt="250px"
          >
            {suggestionResults.map((x, i) => (
              <SuggestionItem key={i} item={x} onClick={handleReply} />
            ))}
          </Col>
        )}

        <Input
          user={user}
          comment
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          handleSend={handleSend}
        />
      </Row>
    </Col>
  );
};

export default Footer;
